import { ComponentProps, RefObject, forwardRef, useRef } from 'react';
import { useMenuTrigger } from 'react-aria';
import { MenuTriggerProps, useMenuTriggerState } from 'react-stately';

import composeRefs from '@seznam/compose-react-refs';

import Button from '~/components/scales/Button/Button';
import Icon from '~/components/scales/Icon/Icon';
import Menu from '~/components/scales/Menu/Menu';
import Popover from '~/components/scales/Popover/Popover';
import { TooltipTriggerProps } from '~/components/scales/Tooltip';
import { validateDataCy } from '~/components/scales/utils/data-cy';

type MenuProps = ComponentProps<typeof Menu> & MenuTriggerProps;

type Props = ComponentProps<typeof Button> &
  MenuProps &
  TooltipTriggerProps & {
    buttonRef?: RefObject<HTMLButtonElement | null>;
    onClick?: never;
    type: 'primary' | 'secondary';
  };

export default forwardRef<HTMLButtonElement, Props>(function MenuButton(props, forwardedRef) {
  const { buttonRef, ...buttonProps } = props;

  // Create state based on the incoming props
  const state = useMenuTriggerState(props);

  // Get props for the menu trigger and menu elements
  const ref = useRef<HTMLButtonElement>(null);
  const { menuProps } = useMenuTrigger({}, state, ref);

  validateDataCy(props['data-cy'], 'menu-button');

  return (
    <div className={`relative ${!props.isFullWidth ? 'w-min' : ''}`}>
      <Button
        {...buttonProps}
        ref={composeRefs(ref, buttonRef, forwardedRef)}
        endIcon={<Icon name="arrow_drop_down" />}
        onClick={() => {
          state.toggle();
        }}
      />
      {state.isOpen && (
        <Popover offset={8} placement="bottom start" state={state} triggerRef={ref}>
          <Menu
            {...menuProps}
            {...props}
            autoFocus={state.focusStrategy || true}
            data-cy={props['data-cy'] ? `${props['data-cy']}-menu` : undefined}
            onClose={state.close}
          />
        </Popover>
      )}
    </div>
  );
});
